import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Firestore } from 'firebase/firestore';
import { getTechnicalTemplatesByCompany, createTechnicalAudit, getTechnicalAuditsByCompany, updateTechnicalAudit, deleteTechnicalAudit, getLocationsByCompany, getUsersByCompany } from '@cmms/api';
import '../common/data-table/data-table.js';

@customElement('gmp-audit-run-page')
export class GmpAuditRunPage extends LitElement {
  static styles = css`
    :host { display: block; background: var(--sl-color-neutral-0); }
    .page-header { display:flex; align-items:center; justify-content:space-between; padding: 1rem 1.5rem 0; }
    .page-title { margin:0; font-size:1.25rem; font-weight:600; color: var(--sl-color-neutral-900); }
    .content { padding: 1rem 1.5rem; display:flex; flex-direction:column; gap: 1rem; }
    .section { background: var(--sl-color-neutral-50); border-radius: 8px; padding: 0.75rem 1rem; box-shadow: var(--sl-shadow-x-small); }
    .section-title { margin:0 0 .5rem 0; font-weight:600; font-size:.95rem; color: var(--sl-color-neutral-800); }
    .question { padding: .5rem 0; }
    .question-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:.25rem; font-size:.85rem; color: var(--sl-color-neutral-700); text-transform: uppercase; letter-spacing:.03em; }
    .actions { display:flex; gap:.5rem; justify-content:flex-end; }
    .select-row { display:flex; gap: .75rem; flex-wrap: wrap; align-items:end; }
    .hint { color: var(--sl-color-neutral-600); font-size: .85rem; }
    sl-divider { margin: .5rem 0; }

    /* Audit report viewer styles */
    .audit-report {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      max-height: 70vh;
      overflow-y: auto;
    }

    .audit-header {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      background: var(--sl-color-neutral-50);
      border: 1px solid var(--sl-color-neutral-200);
      border-radius: 8px;
      padding: 1rem;
    }

    .status-badge {
      padding: .15rem .5rem;
      border-radius: 4px;
      font-size: .8rem;
      text-transform: capitalize;
    }
    .status-badge.completed { background: var(--sl-color-success-100); color: var(--sl-color-success-700); }
    .status-badge.draft { background: var(--sl-color-warning-100); color: var(--sl-color-warning-700); }
    .status-badge.failed { background: var(--sl-color-danger-100); color: var(--sl-color-danger-700); }

    .score {
      margin-left: .5rem;
      font-size: .85rem;
      color: var(--sl-color-neutral-700);
    }

    .report-section {
      border: 1px solid var(--sl-color-neutral-200);
      border-radius: 8px;
      background: var(--sl-color-neutral-0);
      box-shadow: var(--sl-shadow-x-small);
    }

    .section-title {
      font-weight: 600;
      background: var(--sl-color-neutral-50);
      padding: .6rem .9rem;
      border-bottom: 1px solid var(--sl-color-neutral-200);
      color: var(--sl-color-neutral-900);
    }

    .section-content {
      display: flex;
      flex-direction: column;
      padding: .75rem .9rem;
      gap: .75rem;
    }

    .qa-block {
      display: flex;
      flex-direction: column;
      gap: .35rem;
    }

    .question-text {
      font-weight: 500;
      color: var(--sl-color-neutral-900);
    }

    .answer-line {
      display: flex;
      align-items: center;
      gap: .5rem;
      flex-wrap: wrap;
    }

    .answer-badge {
      padding: .2rem .55rem;
      border-radius: 6px;
      font-size: .8rem;
      background: var(--sl-color-neutral-100);
      color: var(--sl-color-neutral-800);
      font-weight: 500;
      text-transform: capitalize;
    }
    .answer-badge.yes { background: var(--sl-color-success-100); color: var(--sl-color-success-700); }
    .answer-badge.no { background: var(--sl-color-danger-100); color: var(--sl-color-danger-700); }

    .comment {
      font-style: italic;
      font-size: .85rem;
      color: var(--sl-color-neutral-700);
    }
    @media (max-width: 768px) { .content { padding: .75rem 1rem; } .page-header { padding: .75rem 1rem 0; } }
  `;

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private templates: any[] = [];
  @state() private templateId: string = '';
  @state() private selectedTemplate: any | null = null;
  @state() private answers: Record<string, any> = {};
  @state() private comments: Record<string, string> = {};
  @state() private isLoading = false;
  @state() private isSaving = false;
  @state() private error: string | null = null;
  @state() private debug: boolean = false;
  @state() private audits: any[] = [];
  @state() private isLoadingAudits = false;
  @state() private showRaw = false;
  @state() private selectedAudit: any = null;
  @state() private showAuditViewer: boolean = false;
  @state() private showDrafts: boolean = false;
  @state() private showRunner: boolean = false;
  @state() private currentAuditId: string = '';
  @state() private locations: any[] = [];
  @state() private users: any[] = [];
  @state() private locationId: string = '';
  @state() private conductedById: string = '';

  connectedCallback(): void {
    super.connectedCallback();
    try {
      const href = String(window.location?.href || '');
      this.debug = href.includes('audit_dbg=1') || localStorage.getItem('cmms_debug_audit') === '1';
    } catch {}
    this.loadTemplates();
    this.loadAudits();
    this.loadLocationsAndUsers();
  }

  updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('companyId')) { this.loadTemplates(); this.loadAudits(); this.loadLocationsAndUsers(); }
  }

  private async loadTemplates() {
    try {
      this.isLoading = true;
      this.error = null;
      if (!this.firestore || !this.companyId) { this.templates = []; return; }
      const resp: any = await getTechnicalTemplatesByCompany(this.firestore, this.companyId, 'GMP');
      this.templates = Array.isArray(resp?.data) ? resp.data : [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load templates';
      this.templates = [];
    } finally { this.isLoading = false; }
  }

  private handleTemplateChange(id: string) {
    this.templateId = id;
    const tpl = this.templates.find(t => t.id === id) || null;
    this.selectedTemplate = tpl ? JSON.parse(JSON.stringify(tpl)) : null;
    this.answers = {};
    this.comments = {};
    this.loadAudits();
    if (this.selectedTemplate) {
      this.showRunner = true;
      this.openRunnerDialogSoon();
    }
  }

  private async openRunnerDialogSoon() {
    await this.updateComplete;
    const dlg: any = this.shadowRoot?.querySelector('#audit-runner');
    if (dlg && typeof dlg.show === 'function') {
      dlg.show();
    }
  }

  private async loadAudits() {
    try {
      if (!this.firestore || !this.companyId) return;
      this.isLoadingAudits = true;
      const opts: any = {};
      if (this.templateId) opts.template_id = this.templateId;
      const resp: any = await getTechnicalAuditsByCompany(this.firestore, this.companyId, opts);
      this.audits = Array.isArray(resp?.data) ? resp.data : [];
    } catch (e) {
    } finally {
      this.isLoadingAudits = false;
    }
  }

  private async loadLocationsAndUsers() {
    try {
      if (!this.firestore || !this.companyId) { this.locations = []; this.users = []; return; }
      const [locResp, usrResp]: any = await Promise.all([
        getLocationsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);
      this.locations = Array.isArray(locResp?.data)
        ? locResp.data.map((d: any) => ({
            id: d.id || d.$id || d?.model?.id,
            name: d.name || d?.model?.name || d?.model?.title || 'Location'
          }))
        : [];
      this.users = Array.isArray(usrResp?.data)
        ? usrResp.data.map((u: any) => {
            const m = u?.model || {};
            const first = (m.first_name || m.firstName || u.first_name || u.firstName || '').trim();
            const last = (m.last_name || m.lastName || u.last_name || u.lastName || '').trim();
            const email = (m.email || u.email || '').trim();
            const name = (m.name || u.name || m.display_name || m.displayName || m.full_name || m.fullName || `${first} ${last}`.trim() || email || 'User').trim();
            return {
              id: u.id || u.$id || m.id || email || name,
              name
            };
          })
        : [];
    } catch (e) {
      this.locations = [];
      this.users = [];
    }
  }

  private setAnswer(sectionIdx: number, qIdx: number, value: any) {
    const key = `${sectionIdx}:${qIdx}`;
    this.answers = { ...this.answers, [key]: value };
  }

  private setComment(sectionIdx: number, qIdx: number, value: string) {
    const key = `${sectionIdx}:${qIdx}`;
    this.comments = { ...this.comments, [key]: value };
  }

  private buildAuditPayload(status: 'draft' | 'completed') {
    const tpl = this.selectedTemplate;
    const sections = (tpl?.sections || []).map((s: any, si: number) => ({
      section_id: s.id || '',
      section_title: s.title || '',
      questions: (s.questions || []).map((q: any, qi: number) => ({
        question_id: q.id || '',
        text: q.text || '',
        type: q.type || 'text',
        // Firestore does not allow undefined values — store null when unanswered
        answer: ((): any => {
          const v = this.answers[`${si}:${qi}`];
          return typeof v === 'undefined' ? null : v;
        })(),
        comment: this.comments[`${si}:${qi}`] || ''
      }))
    }));
    const score = this.calculateScore(tpl, this.answers);
    return {
      template_id: tpl?.id || '',
      template_name: tpl?.name || '',
      status,
      answers: sections,
      score_percent: typeof score === 'number' ? score : null,
      location_id: this.locationId || null,
      location_name: (this.locations.find(l => l.id === this.locationId)?.name) || null,
      conducted_by: this.conductedById || null,
      conducted_by_name: (this.users.find(u => u.id === this.conductedById)?.name) || null
    };
  }

  private calculateScore(template: any, answers: Record<string, any>): number | null {
    if (!template || !Array.isArray(template.sections)) return null;
    let achieved = 0;
    let total = 0;

    (template.sections || []).forEach((s: any, si: number) => {
      (s.questions || []).forEach((q: any, qi: number) => {
        const key = `${si}:${qi}`;
        const val = answers[key];
        if (val == null || val === '') return; // skip unanswered

        switch (q?.type) {
          case 'yes_no': {
            const v = String(val).toLowerCase();
            if (v === 'yes' || v === 'true') { achieved += 1; total += 1; }
            else if (v === 'no' || v === 'false') { achieved += 0; total += 1; }
            break;
          }
          case 'rating':
          case 'number': {
            const num = Math.max(0, Math.min(10, Number(val)));
            if (Number.isFinite(num)) { achieved += num / 10; total += 1; }
            break;
          }
          default:
            // text and multiple_choice are ignored
            break;
        }
      });
    });

    if (total === 0) return null;
    const pct = Math.round((achieved / total) * 100);
    return Math.max(0, Math.min(100, pct));
  }

  private async save(status: 'draft' | 'completed') {
    try {
      if (!this.firestore) { this.error = 'Firestore not available'; return; }
      if (!this.companyId) { this.error = 'Missing companyId'; return; }
      if (!this.selectedTemplate) { this.error = 'Please select a template'; return; }
      this.isSaving = true;
      // Build payload first
      const payload = this.buildAuditPayload(status);
      if (this.debug) {
        const payloadPlain = this.safePlain(payload);
        console.log('[GMP Audit] Saving', status, payloadPlain);
        const snippet = JSON.stringify({
          status,
          template_id: payloadPlain.template_id,
          template_name: payloadPlain.template_name,
          answers_len: Array.isArray(payloadPlain.answers) ? payloadPlain.answers.length : 0,
          score_percent: payloadPlain.score_percent
        }, null, 2);
        const toast = Object.assign(document.createElement('sl-alert'), {
          variant: 'primary', closable: true,
          innerHTML: `<strong>Debug save (${status})</strong><pre style="white-space:pre-wrap;max-height:160px;overflow:auto;font-size:.8rem;">${snippet}</pre>`
        });
        document.body.appendChild(toast); (toast as any).toast?.();
      }
      const resp: any = this.currentAuditId
        ? await updateTechnicalAudit(this.firestore, this.currentAuditId, payload)
        : await createTechnicalAudit(this.firestore, this.companyId, payload);
      const plainResp: any = this.safePlain(resp || {});
      const apiError = (plainResp && plainResp.error) || null;
      if (apiError) {
        const msg = typeof apiError === 'string' ? apiError : (apiError?.message || 'Unknown API error');
        throw new Error(msg);
      }
      const viewData: any = plainResp?.data || {};
      const savedId = viewData?.id || viewData?.$id || '';
      if (this.debug) {
        console.log('[GMP Audit] Save response =>', plainResp);
        const r = JSON.stringify({ id: savedId, keys: Object.keys(plainResp || {}) }, null, 2);
        const t = Object.assign(document.createElement('sl-alert'), {
          variant: 'primary', closable: true,
          innerHTML: `<strong>Debug response</strong><pre style="white-space:pre-wrap;max-height:160px;overflow:auto;font-size:.8rem;">${r}</pre>`
        });
        document.body.appendChild(t); (t as any).toast?.();
      }
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'success', closable: true,
        innerHTML: `<sl-icon slot="icon" name="check-circle"></sl-icon><strong>Audit ${status === 'completed' ? 'submitted' : 'saved'}.</strong> ${savedId ? `ID: ${savedId}` : ''}`
      });
      document.body.appendChild(toast); (toast as any).toast?.();
      // After success, close dialog and reset selection
      this.showRunner = false;
      this.selectedTemplate = null;
      this.templateId = '';
      this.currentAuditId = '';
      await this.loadAudits();
    } catch (e: any) {
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'danger', closable: true,
        innerHTML: `<sl-icon slot="icon" name="exclamation-octagon"></sl-icon><strong>Failed:</strong> ${e?.message || 'Unknown error'}`
      });
      document.body.appendChild(toast); (toast as any).toast?.();
      if (this.debug) {
        console.error('[GMP Audit] Save error', e);
      }
    } finally { this.isSaving = false; }
  }

  private renderAnswerControl(q: any, si: number, qi: number) {
    const key = `${si}:${qi}`;
    const value = this.answers[key];
    switch (q.type) {
      case 'yes_no':
        return html`
          <sl-select label="Answer" .value=${value ?? ''}
            @sl-change=${(e: any) => this.setAnswer(si, qi, e.target.value)}>
            <sl-option value="yes">Yes</sl-option>
            <sl-option value="no">No</sl-option>
          </sl-select>
        `;
      case 'rating':
        return html`<sl-input label="Rating" type="number" min="0" max="10" .value=${value ?? ''}
          @sl-input=${(e: any) => this.setAnswer(si, qi, e.target.value)}></sl-input>`;
      case 'number':
        return html`<sl-input label="Number" type="number" .value=${value ?? ''}
          @sl-input=${(e: any) => this.setAnswer(si, qi, Number(e.target.value))}></sl-input>`;
      case 'multiple_choice':
        return html`
          <sl-select label="Answer" .value=${value ?? ''}
            @sl-change=${(e: any) => this.setAnswer(si, qi, e.target.value)}>
            ${(q.options || []).map((opt: string) => html`<sl-option value=${opt}>${opt}</sl-option>`) }
          </sl-select>
        `;
      case 'text':
      default:
        return html`<sl-textarea label="Answer" .value=${value ?? ''}
          @sl-input=${(e: any) => this.setAnswer(si, qi, e.target.value)}></sl-textarea>`;
    }
  }

  private async openAuditViewer(a: any) {
    // Create a plain, serializable snapshot and normalize
    const plain = this.safePlain(a);
    const norm = this.normalizeAudit(plain);
    this.selectedAudit = norm;
    this.showAuditViewer = true;
    await this.updateComplete;
    const dlg: any = this.shadowRoot?.querySelector('#audit-viewer');
    if (dlg && typeof dlg.show === 'function') {
      dlg.show();
    }
  }

  private normalizeAudit(a: any) {
    const src = a || {};
    const m = (src.model && typeof src.model === 'object') ? src.model : {};
    const norm: any = {
      id: src.id || m.id || '',
      template_id: src.template_id ?? m.template_id ?? '',
      template_name: src.template_name ?? m.template_name ?? '',
      status: src.status ?? m.status ?? '',
      created_at: src.created_at ?? m.created_at ?? null,
      updated_at: src.updated_at ?? m.updated_at ?? null,
      score_percent: src.score_percent ?? m.score_percent ?? null,
      location_id: src.location_id ?? m.location_id ?? null,
      location_name: src.location_name ?? m.location_name ?? null,
      conducted_by: src.conducted_by ?? m.conducted_by ?? null,
      conducted_by_name: src.conducted_by_name ?? m.conducted_by_name ?? null,
      answers: Array.isArray(src.answers) ? src.answers : (Array.isArray(m.answers) ? m.answers : [])
    };
    return norm;
  }

  private openAuditViewerByIndex(e: Event) {
    const i = this.getIndexFromEvent(e);
    const row = i >= 0 ? this.audits[i] : null;
    this.openAuditViewer(row);
  }

  private editAuditByIndex(e: Event) {
    const i = this.getIndexFromEvent(e);
    const row = i >= 0 ? this.audits[i] : null;
    if (row) this.startEditAudit(row);
  }

  private async startEditAudit(a: any) {
    // Normalize legacy audit shapes so we always have answers/template fields
    const norm = this.normalizeAudit(a);
    this.currentAuditId = norm?.id || a?.id || '';
    const tplId = norm?.template_id || '';
    this.templateId = tplId;
    let tpl = this.templates.find(t => t.id === tplId) || null;
    if (!tpl) {
      // Build a pseudo-template from the audit answers if no template found or id missing
      tpl = this.buildTemplateFromAudit(norm);
    }
    this.selectedTemplate = tpl ? JSON.parse(JSON.stringify(tpl)) : null;
    this.answers = {};
    this.comments = {};
    // Populate answers/comments
    if (this.selectedTemplate) {
      this.setAnswersFromAudit(norm, this.selectedTemplate);
    }
    this.showRunner = true;
    await this.openRunnerDialogSoon();
  }

  private buildTemplateFromAudit(audit: any) {
    const sections = (Array.isArray(audit?.answers) ? audit.answers : []).map((s: any, si: number) => ({
      id: s.section_id || `s-${si}`,
      title: s.section_title || `Section ${si + 1}`,
      questions: (Array.isArray(s.questions) ? s.questions : []).map((q: any, qi: number) => ({
        id: q.question_id || `q-${si}-${qi}`,
        text: q.text || '',
        type: q.type || 'text',
        options: q.options || [],
        required: false,
        weight: typeof q.weight === 'number' ? q.weight : undefined,
      }))
    }));
    return {
      id: audit?.template_id || 'ad-hoc',
      name: audit?.template_name || 'Audit',
      sections
    };
  }

  private setAnswersFromAudit(audit: any, template: any) {
    const map: Record<string, any> = {};
    const comm: Record<string, string> = {};
    const aSections: any[] = Array.isArray(audit?.answers) ? audit.answers : [];
    (template.sections || []).forEach((s: any, si: number) => {
      const srcS = aSections.find((ss: any) => ss.section_id && ss.section_id === s.id) || aSections[si] || null;
      const qSrcs: any[] = srcS && Array.isArray(srcS.questions) ? srcS.questions : [];
      (s.questions || []).forEach((q: any, qi: number) => {
        const srcQ = qSrcs.find(qq => qq.question_id && qq.question_id === q.id) || qSrcs[qi] || null;
        if (srcQ) {
          map[`${si}:${qi}`] = srcQ.answer;
          if (srcQ.comment) comm[`${si}:${qi}`] = srcQ.comment;
        }
      });
    });
    this.answers = map;
    this.comments = comm;
  }

  private async deleteAuditByIndex(e: Event) {
    const i = this.getIndexFromEvent(e);
    const row = i >= 0 ? this.audits[i] : null;
    if (!row || !row.id) return;
    const yes = confirm('Delete this audit? This cannot be undone.');
    if (!yes) return;
    try {
      const resp: any = await deleteTechnicalAudit(this.firestore, row.id);
      const apiError = resp?.error || null;
      if (apiError) throw new Error(typeof apiError === 'string' ? apiError : (apiError.message || 'Unknown API error'));
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'success', closable: true,
        innerHTML: '<sl-icon slot="icon" name="check-circle"></sl-icon><strong>Audit deleted.</strong>'
      });
      document.body.appendChild(toast); (toast as any).toast?.();
      await this.loadAudits();
    } catch (e: any) {
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'danger', closable: true,
        innerHTML: `<sl-icon slot="icon" name="exclamation-octagon"></sl-icon><strong>Delete failed:</strong> ${e?.message || 'Unknown error'}`
      });
      document.body.appendChild(toast); (toast as any).toast?.();
    }
  }

  private async deleteAuditDirect(row: any) {
    if (!row || !row.id) return;
    const yes = confirm('Delete this audit? This cannot be undone.');
    if (!yes) return;
    try {
      const resp: any = await deleteTechnicalAudit(this.firestore, row.id);
      const apiError = resp?.error || null;
      if (apiError) throw new Error(typeof apiError === 'string' ? apiError : (apiError.message || 'Unknown API error'));
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'success', closable: true,
        innerHTML: '<sl-icon slot="icon" name="check-circle"></sl-icon><strong>Audit deleted.</strong>'
      });
      document.body.appendChild(toast); (toast as any).toast?.();
      await this.loadAudits();
    } catch (e: any) {
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'danger', closable: true,
        innerHTML: `<sl-icon slot="icon" name="exclamation-octagon"></sl-icon><strong>Delete failed:</strong> ${e?.message || 'Unknown error'}`
      });
      document.body.appendChild(toast); (toast as any).toast?.();
    }
  }

  private getIndexFromEvent(e: Event): number {
    const ct = e.currentTarget as HTMLElement | null;
    let idx = ct?.getAttribute?.('data-index') || null;
    if (!idx && (e as any).composedPath) {
      const path = (e as any).composedPath();
      const host = path.find((n: any) => n && n.getAttribute && n.getAttribute('data-index'));
      idx = host ? host.getAttribute('data-index') : null;
    }
    const i = idx ? parseInt(idx, 10) : -1;
    return isFinite(i) ? i : -1;
  }

  private safePlain<T = any>(obj: any): T {
    try {
      // Replace Firestore Timestamp-like objects
      const replacer = (_k: string, v: any) => {
        if (v && typeof v === 'object') {
          if (typeof v.toDate === 'function') {
            try { return v.toDate().toISOString(); } catch { return String(v); }
          }
        }
        return v;
      };
      return JSON.parse(JSON.stringify(obj || {}, replacer));
    } catch {
      // Fallback: shallow copy enumerable keys
      const plain: any = {};
      if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach(k => { plain[k] = (obj as any)[k]; });
      }
      return plain as T;
    }
  }

  render() {
    return html`
      <div class="page-header">
        <h2 class="page-title">GMP Audit</h2>
      </div>
      <div class="content">
        <sl-card>
          <div class="select-row">
            <sl-select label="Select Template" placeholder="Choose template" .value=${this.templateId}
              @sl-change=${(e: any) => this.handleTemplateChange(e.target.value)}>
              ${this.templates.map(t => html`<sl-option value=${t.id}>${t.name}</sl-option>`) }
            </sl-select>
            ${this.selectedTemplate ? html`<div class="hint">${this.selectedTemplate?.description || ''}</div>` : ''}
          </div>
          ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        </sl-card>

        ${this.selectedTemplate && !this.showRunner ? html`
          ${(this.selectedTemplate.sections || []).map((s: any, si: number) => html`
            <div class="section">
              <h3 class="section-title">${s.title || 'Untitled Section'}</h3>
              ${(s.questions || []).map((q: any, qi: number) => html`
                <div class="question">
                  <div class="question-header">
                    <span>Question ${qi + 1}</span>
                  </div>
                  <sl-input label="Question" .value=${q.text} disabled></sl-input>
                  ${this.renderAnswerControl(q, si, qi)}
                  <sl-textarea label="Comment" .value=${this.comments[`${si}:${qi}`] || ''}
                    @sl-input=${(e: any) => this.setComment(si, qi, e.target.value)}></sl-textarea>
                </div>
                ${qi < ((s.questions?.length || 0) - 1) ? html`<sl-divider></sl-divider>` : ''}
              `)}
            </div>
          `)}

          <div class="actions">
            <sl-button ?disabled=${this.isSaving} @click=${() => this.save('draft')}>
              <sl-icon slot="prefix" name="save"></sl-icon>
              Save Draft
            </sl-button>
            <sl-button variant="primary" ?disabled=${this.isSaving} @click=${() => this.save('completed')}>
              <sl-icon slot="prefix" name="check2-circle"></sl-icon>
              Submit Audit
            </sl-button>
          </div>
        ` : ''}

        <sl-card>
          <div style="display:flex; justify-content:space-between; align-items:center; margin:0 0 .5rem 0;">
            <h3 style="margin:0; font-size:1rem; color: var(--sl-color-neutral-800);">Recent Audits ${this.templateId ? '(Selected Template)' : ''}</h3>
            <sl-switch ?checked=${this.isLoadingAudits ? this.showDrafts : this.showDrafts} @sl-change=${(e: any) => { this.showDrafts = e.target.checked; this.loadAudits(); }}>Show drafts</sl-switch>
          </div>
          ${this.isLoadingAudits ? html`<sl-spinner></sl-spinner>` : html`
            <data-table .config=${this.createAuditsTableConfig()}></data-table>
          `}
        </sl-card>

        <sl-dialog
          id="audit-runner"
          label="Run GMP Audit"
          style="--size: 90ch;"
          ?open=${this.showRunner}
          @sl-hide=${() => { this.showRunner = false; this.selectedTemplate = null; this.templateId = ''; this.currentAuditId = ''; }}
        >
          ${this.selectedTemplate ? html`
            <div class="audit-report"
              @click=${(e: Event) => e.stopPropagation()}
              @sl-input=${(e: Event) => e.stopPropagation()}
              @sl-change=${(e: Event) => e.stopPropagation()}
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
            >
              <div class="audit-header">
                <div><strong>Template:</strong> ${this.selectedTemplate?.name || this.selectedTemplate?.id || '-'}</div>
                <div class="hint">${this.selectedTemplate?.description || ''}</div>
                <div style="display:flex; gap:.75rem; flex-wrap:wrap; margin-top:.5rem;">
                  <sl-select label="Location" placeholder="Select location" .value=${this.locationId}
                    @sl-change=${(e: any) => { this.locationId = e.target.value; }}>
                    ${this.locations.map(l => html`<sl-option value=${l.id}>${l.name}</sl-option>`)}
                  </sl-select>
                  <sl-select label="Conducted by" placeholder="Select user" .value=${this.conductedById}
                    @sl-change=${(e: any) => { this.conductedById = e.target.value; }}>
                    ${this.users.map(u => html`<sl-option value=${u.id}>${u.name}</sl-option>`)}
                  </sl-select>
                </div>
              </div>

              ${(this.selectedTemplate.sections || []).map((s: any, si: number) => html`
                <div class="report-section">
                  <div class="section-title">${s.title || 'Untitled Section'}</div>
                  <div class="section-content">
                    ${(s.questions || []).map((q: any, qi: number) => html`
                      <div class="qa-block">
                        <div class="question-text">Q${qi + 1}. ${q.text || ''}</div>
                        <div>
                          ${this.renderAnswerControl(q, si, qi)}
                          <sl-textarea label="Comment" .value=${this.comments[`${si}:${qi}`] || ''}
                            @sl-input=${(e: any) => this.setComment(si, qi, e.target.value)}></sl-textarea>
                        </div>
                      </div>
                    `)}
                  </div>
                </div>
              `)}
            </div>
          ` : ''}
          <div slot="footer" style="display:flex; gap:.5rem; justify-content:flex-end;">
            <sl-button @click=${() => { this.showRunner = false; }}>Cancel</sl-button>
            <sl-button ?disabled=${this.isSaving} @click=${() => this.save('draft')}>
              <sl-icon slot="prefix" name="save"></sl-icon>
              Save Draft
            </sl-button>
            <sl-button variant="primary" ?disabled=${this.isSaving} @click=${() => this.save('completed')}>
              <sl-icon slot="prefix" name="check2-circle"></sl-icon>
              Submit Audit
            </sl-button>
          </div>
        </sl-dialog>

        <sl-dialog
          id="audit-viewer"
          label="Audit Report"
          style="--size: 90ch;"
          ?open=${this.showAuditViewer}
          @sl-hide=${() => { this.showAuditViewer = false; this.selectedAudit = null; }}
        >
          ${(() => {
            const a: any = this.normalizeAudit(this.selectedAudit);
            const sections = Array.isArray(a.answers) ? a.answers : [];
            return html`
              <div class="audit-report">
                <div class="audit-header">
                  <div><strong>Template:</strong> ${a.template_name || a.template_id || '-'}</div>
                  <div><strong>Status:</strong>
                    <span class="status-badge ${a.status}">${a.status || '-'}</span>
                    <span class="score">Score: ${a.score_percent != null ? `${a.score_percent}%` : '—'}</span>
                  </div>
                  <div><strong>Created:</strong> ${a.created_at || '-'}</div>
                  ${a.location_name || a.location_id ? html`<div><strong>Location:</strong> ${a.location_name || a.location_id}</div>` : ''}
                  ${a.conducted_by_name || a.conducted_by ? html`<div><strong>Conducted by:</strong> ${a.conducted_by_name || a.conducted_by}</div>` : ''}
                </div>

                ${sections.length ? sections.map((s: any, si: number) => html`
                  <div class="report-section">
                    <div class="section-title">${s.section_title || 'Section ' + (si + 1)}</div>
                    <div class="section-content">
                      ${(s.questions || []).map((q: any, qi: number) => html`
                        <div class="qa-block">
                          <div class="question-text">Q${qi + 1}. ${q.text || ''}</div>
                          <div class="answer-line">
                            <span class="answer-badge ${String(q.answer).toLowerCase()}">${q.answer ?? '-'}</span>
                            ${q.comment ? html`<span class="comment">“${q.comment}”</span>` : ''}
                          </div>
                        </div>
                      `)}
                    </div>
                  </div>
                `) : html`<div class="hint">No details available for this audit.</div>`}
              </div>
            `;
          })()}

          <div slot="footer" style="display:flex; justify-content:flex-end;">
            <sl-button @click=${() => { this.showAuditViewer = false; this.selectedAudit = null; }}>Close</sl-button>
          </div>
        </sl-dialog>
      </div>
    `;
  }

  private createAuditsTableConfig() {
    const data = Array.isArray(this.audits) ? this.audits : [];
    const dateStr = (v: any) => {
      try { return v?.toDate ? v.toDate().toLocaleString() : (typeof v === 'number' ? new Date(v).toLocaleString() : (v || '')); } catch { return ''; }
    };
    return {
      data,
      columns: [
        { key: 'template_name', label: 'Template', type: 'custom', sortable: true, renderer: (_: any, a: any) => html`<span class="primary">${a.template_name || a.template_id || 'Audit'}</span>` },
        { key: 'status', label: 'Status', type: 'custom', sortable: true, width: '120px', renderer: (v: any, a: any) => html`<sl-badge pill variant=${(a.status === 'completed' ? 'success' : (a.status === 'draft' ? 'warning' : 'neutral'))}>${a.status || 'unknown'}</sl-badge>` },
        { key: 'score_percent', label: 'Score', type: 'custom', width: '100px', renderer: (v: any, a: any) => html`${a.score_percent != null ? `${a.score_percent}%` : '—'}` },
        { key: 'created_at', label: 'Created', type: 'custom', width: '180px', renderer: (v: any, a: any) => html`${dateStr(a.created_at)}` },
        { key: 'actions', label: 'Actions', type: 'custom', width: '240px', renderer: (_: any, a: any) => html`
            <div class="row-actions" style="display:flex; gap:.35rem; justify-content:flex-end;">
              <sl-button size="small" @click=${() => this.openAuditViewer(a)}>View</sl-button>
              <sl-button size="small" variant="neutral" @click=${() => this.startEditAudit(a)}>Edit</sl-button>
              <sl-button size="small" variant="danger" @click=${() => this.deleteAuditDirect(a)}>Delete</sl-button>
            </div>
          ` },
      ],
      // Make entire row clickable to open the viewer
      clickableRows: true,
      searchPlaceholder: 'Search audits...',
      pagination: { enabled: true, dynamic: false },
      emptyState: { icon: 'info-circle', message: 'No audits yet.' },
      onRowClick: (row: any) => this.openAuditViewer(row),
      defaultSort: { key: 'created_at', direction: 'desc' }
    } as any;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gmp-audit-run-page': GmpAuditRunPage;
  }
}
